import React, { useState, useEffect } from 'react';
import { Handshake, Plus, Trash2, Edit, Crown, Award, Gem, Star, Globe, ExternalLink, Upload, X, Eye, EyeOff, Save, Search } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  if (path.startsWith('/api/uploads/')) return `${BACKEND_URL}${path}`;
  if (path.startsWith('/uploads/')) return `${BACKEND_URL}/api${path}`;
  return `${BACKEND_URL}${path}`;
};

const PartnersManagementPage = ({ getAuthHeaders, currentUser }) => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [failedLogos, setFailedLogos] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '', description: '', detailed_description: '', logo_url: '', website_url: '',
    partner_type: 'partner', tier: 'standard', featured: false, contact_email: '',
    social_links: { twitter: '', linkedin: '', instagram: '', discord: '', roblox_group: '', roblox_discord: '' }, benefits: [''], is_active: true
  });

  useEffect(() => { fetchPartners(); }, []);

  const fetchPartners = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const response = await axios.get(`${BACKEND_URL}/api/partners/admin`, { headers });
      setPartners(response.data);
    } catch (error) {
      if (error.response?.status === 403) toast.error('Superadmin access required');
    } finally { setLoading(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    setUploading(true);
    try {
      const headers = getAuthHeaders();
      const formDataUpload = new FormData();
      formDataUpload.append('logo', file);
      const response = await axios.post(`${BACKEND_URL}/api/partners/upload-logo`, formDataUpload, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setFormData({ ...formData, logo_url: response.data.logo_url });
      toast.success('Logo uploaded!');
    } catch (error) { toast.error('Failed to upload logo'); }
    finally { setUploading(false); }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.description) { toast.error('Name and description are required'); return; }
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const cleanedBenefits = formData.benefits.filter(b => b.trim() !== '');
      const cleanedSocialLinks = {};
      Object.entries(formData.social_links).forEach(([key, value]) => { if (value.trim()) cleanedSocialLinks[key] = value.trim(); });
      const payload = { ...formData, benefits: cleanedBenefits.length > 0 ? cleanedBenefits : null, social_links: Object.keys(cleanedSocialLinks).length > 0 ? cleanedSocialLinks : null };
      if (editingPartner) {
        await axios.put(`${BACKEND_URL}/api/partners/admin/${editingPartner.id}`, payload, { headers });
        toast.success('Affiliate updated!');
      } else {
        await axios.post(`${BACKEND_URL}/api/partners/admin`, payload, { headers });
        toast.success('Affiliate created!');
      }
      setShowForm(false); setEditingPartner(null); resetForm(); fetchPartners();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save affiliate'); }
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name || '', description: partner.description || '', detailed_description: partner.detailed_description || '',
      logo_url: partner.logo_url || '', website_url: partner.website_url || '', partner_type: partner.partner_type || 'partner',
      tier: partner.tier || 'standard', featured: partner.featured || false, contact_email: partner.contact_email || '',
      social_links: partner.social_links || { twitter: '', linkedin: '', instagram: '', discord: '', roblox_group: '', roblox_discord: '' },
      benefits: partner.benefits?.length > 0 ? partner.benefits : [''], is_active: partner.is_active !== false
    });
    setShowForm(true);
  };

  const handleDelete = async (partnerId, partnerName) => {
    if (!window.confirm(`Delete "${partnerName}"?`)) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${BACKEND_URL}/api/partners/admin/${partnerId}`, { headers });
      toast.success('Affiliate deleted'); fetchPartners();
    } catch (error) { toast.error('Failed to delete affiliate'); }
  };

  const toggleActive = async (partner) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${BACKEND_URL}/api/partners/admin/${partner.id}`, { is_active: !partner.is_active }, { headers });
      toast.success(partner.is_active ? 'Hidden from public' : 'Now visible'); fetchPartners();
    } catch (error) { toast.error('Failed to update'); }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', detailed_description: '', logo_url: '', website_url: '', partner_type: 'partner', tier: 'standard', featured: false, contact_email: '', social_links: { twitter: '', linkedin: '', instagram: '', discord: '', roblox_group: '', roblox_discord: '' }, benefits: [''], is_active: true });
  };

  const addBenefit = () => setFormData({ ...formData, benefits: [...formData.benefits, ''] });
  const updateBenefit = (i, val) => { const b = [...formData.benefits]; b[i] = val; setFormData({ ...formData, benefits: b }); };
  const removeBenefit = (i) => { const b = formData.benefits.filter((_, idx) => idx !== i); setFormData({ ...formData, benefits: b.length > 0 ? b : [''] }); };

  const getTierIcon = (tier) => {
    if (tier === 'platinum') return <Crown className="w-4 h-4 text-slate-200" />;
    if (tier === 'gold') return <Award className="w-4 h-4 text-yellow-400" />;
    if (tier === 'silver') return <Gem className="w-4 h-4 text-slate-400" />;
    return <Star className="w-4 h-4 text-cyan-400" />;
  };

  const getTierBadgeClass = (tier) => {
    if (tier === 'platinum') return 'bg-gradient-to-r from-slate-300/90 to-slate-100/90 text-slate-900';
    if (tier === 'gold') return 'bg-gradient-to-r from-yellow-400/90 to-amber-500/90 text-black';
    if (tier === 'silver') return 'bg-gradient-to-r from-slate-300/80 to-slate-400/80 text-slate-900';
    return 'bg-cyan-500/20 text-cyan-400';
  };

  if (currentUser?.role !== 'superadmin') {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center"><Crown className="w-14 h-14 text-amber-400 mx-auto mb-4" /><p className="text-slate-400">Superadmin access required</p></div>
      </div>
    );
  }

  const inputCls = "w-full px-4 py-3 bg-slate-900/80 border border-slate-700/50 rounded-xl text-white focus:outline-none focus:border-amber-500/50 placeholder-slate-600 text-sm";
  const labelCls = "block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider";

  const filtered = partners.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()) || (p.description || '').toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="partners-management-page">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-900 via-amber-950/30 to-slate-900 border border-amber-500/20 p-6">
        <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl" />
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500/30 to-orange-600/30 flex items-center justify-center border border-amber-500/20">
              <Handshake className="w-7 h-7 text-amber-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Affiliates</h1>
              <p className="text-slate-400 text-sm">Manage your affiliate network for the public website</p>
            </div>
          </div>
          <Button onClick={() => { resetForm(); setEditingPartner(null); setShowForm(true); }} className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-lg shadow-amber-500/20">
            <Plus className="w-4 h-4 mr-2" /> Add Affiliate
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total', value: partners.length, icon: Handshake, color: 'amber' },
          { label: 'Featured', value: partners.filter(p => p.featured).length, icon: Star, color: 'yellow' },
          { label: 'Active', value: partners.filter(p => p.is_active).length, icon: Eye, color: 'emerald' },
          { label: 'Premium', value: partners.filter(p => p.tier === 'platinum' || p.tier === 'gold').length, icon: Crown, color: 'purple' },
        ].map((stat, i) => (
          <div key={i} className="bg-slate-900/60 border border-slate-800/50 rounded-xl p-4 flex items-center gap-4">
            <div className={`w-11 h-11 rounded-xl bg-${stat.color}-500/20 flex items-center justify-center`}>
              <stat.icon className={`w-5 h-5 text-${stat.color}-400`} />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stat.value}</p>
              <p className="text-slate-500 text-xs">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search affiliates..."
          className="w-full pl-10 pr-4 py-2.5 bg-slate-900/60 border border-slate-700/50 rounded-xl text-white text-sm placeholder-slate-500 focus:outline-none focus:border-amber-500/50" />
      </div>

      {/* Partners Grid */}
      {loading ? (
        <div className="text-center py-16 text-slate-500">
          <div className="w-10 h-10 border-3 border-amber-500/30 border-t-amber-500 rounded-full animate-spin mx-auto mb-3" />Loading...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-slate-900/40 border border-slate-800/50 rounded-2xl">
          <Handshake className="w-14 h-14 text-slate-700 mx-auto mb-3" />
          <p className="text-slate-500 mb-4">{searchTerm ? 'No matching affiliates' : 'No affiliates yet'}</p>
          {!searchTerm && <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/30"><Plus className="w-4 h-4 mr-2" />Add First Affiliate</Button>}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {filtered.map((partner) => (
            <div key={partner.id} data-testid={`partner-card-${partner.id}`}
              className={`group bg-slate-900/60 border rounded-2xl p-5 hover:shadow-lg transition-all ${partner.is_active ? 'border-slate-800/50 hover:border-amber-500/30 hover:shadow-amber-500/5' : 'border-slate-800/30 opacity-60'}`}>
              <div className="flex items-start gap-4">
                {/* Logo */}
                {partner.logo_url && !failedLogos[partner.id] ? (
                  <div className="w-14 h-14 rounded-xl bg-white/10 p-2 flex items-center justify-center overflow-hidden flex-shrink-0">
                    <img src={getImageUrl(partner.logo_url)} alt={partner.name} className="max-w-full max-h-full object-contain"
                      onError={() => setFailedLogos(prev => ({ ...prev, [partner.id]: true }))} />
                  </div>
                ) : (
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center flex-shrink-0">
                    <Handshake className="w-7 h-7 text-amber-400" />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-white font-semibold truncate">{partner.name}</h3>
                    {partner.featured && <Star className="w-4 h-4 text-yellow-400 fill-yellow-400 flex-shrink-0" />}
                    {!partner.is_active && <Badge className="bg-red-500/20 text-red-400 text-[10px]">Hidden</Badge>}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    <Badge className={getTierBadgeClass(partner.tier) + ' text-[10px] flex items-center gap-1'}>
                      {getTierIcon(partner.tier)}{partner.tier.charAt(0).toUpperCase() + partner.tier.slice(1)}
                    </Badge>
                    <Badge className="bg-purple-500/20 text-purple-400 text-[10px]">{partner.partner_type.charAt(0).toUpperCase() + partner.partner_type.slice(1)}</Badge>
                  </div>
                  <p className="text-slate-500 text-sm line-clamp-2">{partner.description}</p>
                  {partner.website_url && (
                    <a href={partner.website_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-amber-400 text-xs mt-2 hover:text-amber-300">
                      <Globe className="w-3 h-3" />Website<ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => toggleActive(partner)} className={`p-1.5 rounded-lg hover:bg-slate-800 ${partner.is_active ? 'text-emerald-400' : 'text-slate-400'}`}>
                    {partner.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button onClick={() => handleEdit(partner)} className="p-1.5 rounded-lg hover:bg-slate-800 text-amber-400"><Edit className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(partner.id, partner.name)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => { if (!open) { setShowForm(false); setEditingPartner(null); resetForm(); } }}>
        <DialogContent className="bg-slate-950 border-amber-500/20 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-amber-400 flex items-center gap-2">
              <Handshake className="w-5 h-5" />{editingPartner ? 'Edit Affiliate' : 'Add New Affiliate'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className={labelCls}>Name *</label><input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className={inputCls} placeholder="Company name" /></div>
              <div><label className={labelCls}>Website</label><input type="url" value={formData.website_url} onChange={(e) => setFormData({ ...formData, website_url: e.target.value })} className={inputCls} placeholder="https://example.com" /></div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className={labelCls}>Type</label>
                <select value={formData.partner_type} onChange={(e) => setFormData({ ...formData, partner_type: e.target.value })} className={inputCls}>
                  {['partner', 'affiliate', 'sponsor'].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
              <div><label className={labelCls}>Tier</label>
                <select value={formData.tier} onChange={(e) => setFormData({ ...formData, tier: e.target.value })} className={inputCls}>
                  {['platinum', 'gold', 'silver', 'standard'].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
            </div>

            {/* Logo */}
            <div>
              <label className={labelCls}>Logo</label>
              <div className="flex items-center gap-4">
                {formData.logo_url && (
                  <div className="w-16 h-16 rounded-xl bg-white/10 p-2 flex items-center justify-center overflow-hidden">
                    <img src={getImageUrl(formData.logo_url)} alt="" className="max-w-full max-h-full object-contain" />
                  </div>
                )}
                <label className="flex items-center gap-2 px-4 py-2.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-xl cursor-pointer transition-colors text-sm border border-amber-500/20">
                  <Upload className="w-4 h-4" />{uploading ? 'Uploading...' : 'Upload Logo'}
                  <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" disabled={uploading} />
                </label>
              </div>
            </div>

            <div><label className={labelCls}>Short Description *</label><textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} className={inputCls + ' resize-none'} placeholder="Brief description..." /></div>
            <div><label className={labelCls}>Detailed Description</label><textarea value={formData.detailed_description} onChange={(e) => setFormData({ ...formData, detailed_description: e.target.value })} rows={3} className={inputCls + ' resize-none'} placeholder="Full description..." /></div>

            {/* Benefits */}
            <div>
              <label className={labelCls}>Benefits</label>
              <div className="space-y-2">
                {formData.benefits.map((benefit, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input type="text" value={benefit} onChange={(e) => updateBenefit(i, e.target.value)} className={inputCls} placeholder="Enter benefit" />
                    <button onClick={() => removeBenefit(i)} className="p-2 text-slate-500 hover:text-red-400"><X className="w-4 h-4" /></button>
                  </div>
                ))}
                <button onClick={addBenefit} className="w-full py-2 border border-dashed border-slate-700 rounded-xl text-slate-500 hover:text-amber-400 hover:border-amber-500/30 text-sm flex items-center justify-center gap-1">
                  <Plus className="w-3 h-3" />Add Benefit
                </button>
              </div>
            </div>

            {/* Social */}
            <div>
              <label className={labelCls}>Social & Community Links</label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { k: 'twitter', p: 'Twitter / X URL' },
                  { k: 'linkedin', p: 'LinkedIn URL' },
                  { k: 'instagram', p: 'Instagram URL' },
                  { k: 'discord', p: 'Discord invite URL' },
                  { k: 'roblox_group', p: 'Roblox group URL' },
                  { k: 'roblox_discord', p: 'Roblox community Discord' },
                ].map(({ k, p }) => (
                  <input key={k} type="url" value={formData.social_links[k] || ''} onChange={(e) => setFormData({ ...formData, social_links: { ...formData.social_links, [k]: e.target.value } })}
                    className={inputCls} placeholder={p} />
                ))}
              </div>
            </div>

            {/* Toggles */}
            <div className="flex flex-wrap gap-6">
              {[
                { key: 'featured', label: 'Featured', icon: Star, color: 'yellow' },
                { key: 'is_active', label: 'Active (public)', color: 'emerald' },
              ].map(({ key, label, icon: Icon, color }) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData[key]} onChange={(e) => setFormData({ ...formData, [key]: e.target.checked })} className="w-4 h-4 rounded bg-slate-800 border-slate-600 text-amber-500 focus:ring-amber-500/50" />
                  <span className="text-slate-300 text-sm flex items-center gap-1">{Icon && <Icon className={`w-3.5 h-3.5 text-${color}-400`} />}{label}</span>
                </label>
              ))}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
              <Button onClick={() => { setShowForm(false); setEditingPartner(null); resetForm(); }} variant="outline" className="border-slate-700 text-slate-400">Cancel</Button>
              <Button onClick={handleSubmit} className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-lg shadow-amber-500/20">
                <Save className="w-4 h-4 mr-2" />{editingPartner ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartnersManagementPage;
